/* Tijd, gerekend in de tijdzone van de locatie.
 *
 * Port van greenvision-ems/web/src/data/tijd.js, met één verschil: daar stond de
 * zone als constante in dit bestand. Hier komt hij uit ems.locatie.tijdzone en
 * gaat hij als argument mee. Er is bewust géén standaardwaarde — een ontbrekende
 * zone moet omvallen, niet stil terugvallen op de zone van de browser of op een
 * vaste zone. Dat laatste zou bij een tweede klant in een andere zone kwartieren
 * op de verkeerde dag zetten zonder dat iemand het ziet.
 *
 * De database levert timestamptz, de browser staat in een willekeurige zone, dus
 * wordt hier één keer omgerekend en verder nergens meer.
 */

export const Q = 96; // kwartieren per etmaal

const FORMATTERS = new Map<string, Intl.DateTimeFormat>();

/** Gooit bij een lege of onbekende zone. Intl zelf gooit RangeError bij onzin. */
export function eisZone(zone: unknown): string {
  if (typeof zone !== 'string' || zone.trim() === '') {
    throw new Error('tijdzone ontbreekt: ems.locatie.tijdzone moet gevuld zijn');
  }
  return zone;
}

function formatter(zone: string): Intl.DateTimeFormat {
  const z = eisZone(zone);
  let f = FORMATTERS.get(z);
  if (!f) {
    f = new Intl.DateTimeFormat('en-CA', {
      timeZone: z, hour12: false,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
    FORMATTERS.set(z, f);
  }
  return f;
}

function delen(d: Date, zone: string): Record<string, string> {
  const p: Record<string, string> = {};
  for (const x of formatter(zone).formatToParts(d)) if (x.type !== 'literal') p[x.type] = x.value;
  return p;
}

/* Offset van de zone op dat moment, in minuten. */
function offsetMin(d: Date, zone: string): number {
  const p = delen(d, zone);
  const alsUtc = Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour % 24, +p.minute, +p.second);
  return (alsUtc - d.getTime()) / 60000;
}

/**
 * Een lokale kloktijd als UTC-instant, met de regel die Postgres hanteert voor
 * `timestamp at time zone`:
 *   - bestaat de kloktijd twee keer (klok terug): de offset van ná de overgang;
 *   - bestaat hij niet (klok vooruit): de offset van vóór de overgang.
 *
 * Nagerekend in Postgres 17.4 op 12-09-2026, zie tests/ems/tijdzone.node.test.mjs.
 *
 * Afwijking van de bron: tijd.js in greenvision-ems rekende in twee slagen vanaf
 * UTC-middernacht. Met een vaste Europe/Amsterdam gaf dat hetzelfde als Postgres,
 * maar in een zone waar de klok óm middernacht vooruit gaat (Havana, Santiago)
 * kwam hij een uur te vroeg uit — en de zone komt nu uit de locatie.
 *
 * @param wand  de kloktijd als milliseconden, gelezen alsof hij UTC is
 */
export function lokaleTijdNaarInstant(wand: number, zone: string): Date {
  // Offsets liggen tussen -12 en +14 uur, dus de kandidaten ook. Meet net
  // daarbuiten; overgangen liggen maanden uit elkaar, dus er valt er hoogstens één tussen.
  const voor = offsetMin(new Date(wand - 15 * 3600_000), zone);
  const na = offsetMin(new Date(wand + 13 * 3600_000), zone);
  const metNa = wand - na * 60_000;
  if (voor === na || offsetMin(new Date(metNa), zone) === na) return new Date(metNa);
  return new Date(wand - voor * 60_000);
}

/* Het moment waarop in de zone een nieuwe dag begint, als UTC-instant. De
   tegenhanger van `($dag::date)::timestamp at time zone l.tijdzone` in de SQL. */
export function beginVanDag(dagSleutel: string, zone: string): Date {
  return lokaleTijdNaarInstant(Date.parse(`${dagSleutel}T00:00:00Z`), zone);
}

/* Lokale dagsleutel (YYYY-MM-DD) en kwartierindex 0..95 van een tijdstip. */
export function lokaal(tijdstip: string | Date, zone: string): { dag: string; i: number } {
  const d = tijdstip instanceof Date ? tijdstip : new Date(tijdstip);
  const p = delen(d, zone);
  return {
    dag: `${p.year}-${p.month}-${p.day}`,
    i: (+p.hour % 24) * 4 + Math.floor(+p.minute / 15),
  };
}

/* Dagsleutel van nu, in de zone van de locatie. */
export function vandaagSleutel(zone: string, nu: Date = new Date()): string {
  return lokaal(nu, zone).dag;
}

/* Dagsleutel n dagen verder. Rekent over de kalender, niet over 24-uursblokken,
   dus zomertijd verschuift niets. Zoneloos. */
export function dagSleutelPlus(sleutel: string, dagen: number): string {
  const [j, m, d] = sleutel.split('-').map(Number);
  const t = new Date(Date.UTC(j, m - 1, d));
  t.setUTCDate(t.getUTCDate() + dagen);
  return `${t.getUTCFullYear()}-${String(t.getUTCMonth() + 1).padStart(2, '0')}-${String(t.getUTCDate()).padStart(2, '0')}`;
}

/* Dagsleutel als JS-Date op lokale middernacht van de browser, zodat
   getDate(), getMonth() en getDay() de dag van de locatie teruggeven. */
export function sleutelNaarDatum(sleutel: string): Date {
  const [j, m, d] = sleutel.split('-').map(Number);
  return new Date(j, m - 1, d);
}

/* Oplopende lijst dagsleutels, van inbegrepen tot en met tot. */
export function dagReeks(van: string, tot: string): string[] {
  const uit: string[] = [];
  let s = van;
  for (let n = 0; n < 4000 && s <= tot; n++) { uit.push(s); s = dagSleutelPlus(s, 1); }
  return uit;
}

/** Is dit een geldige JJJJ-MM-DD. Zelfde regel als isDagsleutel in lib/poort.ts. */
export function isDagsleutel(waarde: unknown): boolean {
  if (typeof waarde !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(waarde)) return false;
  const d = new Date(`${waarde}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === waarde;
}

export function dagenTussen(van: string, tot: string): number {
  const a = Date.parse(`${van}T00:00:00Z`);
  const b = Date.parse(`${tot}T00:00:00Z`);
  return Math.round((b - a) / 86_400_000) + 1;
}
